'use client'

/* eslint-disable react/hook-use-state */
import { noop } from 'lodash'
import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

import { UiState } from '@marketplace-web/shared/ui-state'
import { Screen } from 'constants/tracking/screens'

import { UiStateContextType } from '../types/context'
import { ItemStatus } from '../constants'

export const UiStateContext = createContext<UiStateContextType>({
  itemStatus: [ItemStatus.New, noop],
  screen: Screen.ItemUpload,
  errors: [{ byName: {}, names: [] }, noop],
  setUiState: noop,
  uiState: UiState.Pending,
  price: {
    hasSimilarItems: [false, noop],
  },
  canBumpItem: [false, noop],
  visibleFields: [],
  updateFieldVisibility: noop,
})

export const UiStateContextProvider = ({ children }: { children: ReactNode }) => {
  const [uiState, setUiState] = useState<UiState>(UiState.Pending)
  const [visibleFields, setVisibleFields] = useState<Array<string>>([])
  const itemStatus = useState<ItemStatus>(ItemStatus.New)
  const errors = useState<{ byName: { [fieldName: string]: string }; names: Array<string> }>({
    byName: {},
    names: [],
  })
  const hasSimilarItems = useState(false)
  const canBumpItem = useState(false)

  const screen = useMemo(() => {
    switch (itemStatus[0]) {
      case ItemStatus.Edit:
        return Screen.ItemEdit
      case ItemStatus.DraftEdit:
        return Screen.ItemDraft
      default:
        return Screen.ItemUpload
    }
  }, [itemStatus])

  const updateFieldVisibility = useCallback(
    ({ fieldName, isVisible }: { fieldName: string; isVisible: boolean }) => {
      setVisibleFields(prev => {
        const fields = new Set(prev)
        if (isVisible) {
          fields.add(fieldName)
        } else {
          fields.delete(fieldName)
        }

        return Array.from(fields)
      })
    },
    [],
  )

  const providerValue = useMemo(
    () => ({
      itemStatus,
      screen,
      errors,
      price: { hasSimilarItems },
      setUiState,
      uiState,
      canBumpItem,
      visibleFields,
      updateFieldVisibility,
    }),
    [
      itemStatus,
      screen,
      errors,
      hasSimilarItems,
      setUiState,
      uiState,
      canBumpItem,
      visibleFields,
      updateFieldVisibility,
    ],
  )

  return <UiStateContext.Provider value={providerValue}>{children}</UiStateContext.Provider>
}

export const useUiStateContext = () => {
  const context = useContext(UiStateContext)

  if (!context) {
    throw new Error('useUiStateContext must be used within a UiStateContextProvider')
  }

  return context
}
